<template>
    <BaseWidget
        :id="id"
        :widget-name="widgetName"
        :has-content="!!content"
        :is-loading="isLoading"
        :is-expanded="isExpanded"
        :configuration-data="configurationData"
        :widget-type="widgetType"
        :widget-last-updated="widgetLastUpdated"
        :needs-attention="needsAttention"
        v-on="$listeners"
    >
        <div v-show="!isLoading" class="widget-content">
            <div class="widget-inner">
                <div class="wrapper-float">
                    <div v-if="isExpanded" class="inline">{{ $t('reporting.commonLabels.callType') }}:</div>
                    <div class="widget-selectbox-wrapper" @mousedown.stop.prevent>
                        <SelectBox
                            :value="selectedOption"
                            :options="callTypesOptions"
                            style-class="inline"
                            :search="false"
                            @input="setSelectedOptions({ widgetId: id, selectedOption: $event })"
                        />
                    </div>
                </div>
                <div v-show="chartDataValid" class="pie-chart-wrapper target-time-pie-chart">
                    <div ref="widgetChartRef" class="doughnut-chart-3"></div>
                </div>
                <div v-show="!chartDataValid" class="no-widget">
                    <div class="no-widget-data">
                        <h2>{{ $t('common.noData') }}</h2>
                        <p>{{ $t('dashboard.otherConfiguration') }}</p>
                    </div>
                </div>
            </div>
        </div>
    </BaseWidget>
</template>

<script type="text/babel">
import { mapState, mapMutations, mapGetters } from 'vuex';
import Chartist from 'ascom-chartist-logscale';
import BaseWidget from '../BaseWidget';
import SelectBox from '../../common/SelectBox.vue';
import widgetInterface from './common/widgetInterface';
import WidgetConfigurationTable from './common/WidgetConfigurationTable.vue';
import { mounted, beforeDestroy } from './common/chartsLifecycleHooks';
import { initDoughnutChart, makeChart } from './common/chartingHelpers';
import { configurationData } from './common/widgetsComputedProps';

export default {
    components: {
        BaseWidget,
        SelectBox,
        WidgetConfigurationTable,
    },

    props: widgetInterface,

    data() {
        return {
            defaultCall: 1,
            instanceDoughnutChart: null,
            needsAttention: false,
        };
    },

    computed: {
        ...mapGetters({
            userType: 'user/userType',
            getSelectedOptionById: 'dashboard/getSelectedOptionById',
        }),

        ...mapState({
            navCollapsed: 'navCollapsed',
            callTypes: 'callTypes',
            callTypesOptions: state => state.callTypes.map(({ id, name }) => ({ value: id, name })),
            allAssignmentAreas: 'assignmentArea',
        }),

        /**
         * Returns widget selected option
         * @returns {number}
         */
        selectedOption() {
            return this.getSelectedOptionById(this.id) || this.defaultCall;
        },

        configurationData,

        /**
         * Returns widget name
         * @returns {string}
         */
        widgetName() {
            return this.configuration.name || this.$t('dashboard.widgetType3.name');
        },

        /**
         * Checks if there is something to display on the chart
         * @returns {Boolean}
         */
        chartDataValid() {
            return (
                !this.content || this.content.find(e => e.call_type === this.selectedOption).quantity !== null
            );
        },

        /**
         * Prepares data for Chartist
         * @returns {array}
         */
        chartData() {
            if (!this.content) {
                return [];
            }

            let quantity = this.content.find(e => e.call_type === this.selectedOption).quantity || [];

            return [
                { meta: this.$t('dashboard.widgetType3.onTime'), value: quantity[0], className: 'ct-target' },
                {
                    meta: this.$t('dashboard.widgetType3.delayed'),
                    value: quantity[1],
                    className: 'ct-delayed',
                },
                { meta: this.$t('dashboard.widgetType3.late'), value: quantity[2], className: 'ct-late' },
            ];
        },
    },

    mounted,

    beforeDestroy,

    methods: {
        ...mapMutations({
            setSelectedOptions: 'dashboard/SET_SELECTED_WIDGET_DATA',
        }),

        makeChart,

        drawChart(ref, data) {
            let options = initDoughnutChart(data, this.isExpanded, this.isPrintver);

            if (this.instanceDoughnutChart) {
                this.instanceDoughnutChart.update({ series: data }, options);
            } else {
                this.instanceDoughnutChart = new Chartist.Pie(ref, { series: data }, options);
            }
        },

        setNeedsAttention(value) {
            this.needsAttention = value;
        },
    },
};
</script>
